import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi, useAuth } from 'hooks';
import { FeedbackOption, FeedbackType, Sentiment } from 'models/userFeedback';
import { useParams } from 'react-router-dom';

type CareOSBlockSurvey = {
  symptoms_feedback: FeedbackOption | null;
  implementation_feedback: FeedbackOption | null;
  notes: string | null;
};

type ChiefComplaintSurvey = {
  sentiment: Sentiment;
};

type Payload = {
  user_block_id: string | null;
  survey: CareOSBlockSurvey | ChiefComplaintSurvey;
  survey_type: FeedbackType | null;
  feedback_day: string | null;
};

export const useCreateUserFeedback = () => {
  const queryClient = useQueryClient();
  const { patient: userId } = useParams();
  const { api } = useApi({ throwIfError: true });
  const { userToken } = useAuth();

  const mutation = useMutation({
    mutationFn: async ({
      user_block_id,
      survey,
      survey_type,
      feedback_day,
    }: Payload) => {
      return await api({
        route: '/private/feedback/user-survey',
        method: 'POST',
        token: userToken,
        payload: {
          survey,
          survey_type,
          user_block_id,
          feedback_day,
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Coach-Override': userId ?? '',
        },
      });
    },
    onSuccess: (_, { user_block_id }) => {
      // Queries without a block key it as undefined, so map null to match them.
      queryClient.invalidateQueries({
        queryKey: [
          'user-feedback',
          userToken,
          userId,
          user_block_id ?? undefined,
        ],
      });
    },
  });

  return {
    mutation,
  };
};

export default useCreateUserFeedback;
